import { ExpandLess } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMore from "@mui/icons-material/ExpandMore";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import LoadingButton from "@mui/lab/LoadingButton";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import {
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  useGetAccountOnboardingStatusQuery,
  useGetDocumentsQuery,
  useSubmitAccountMutation,
} from "api/api-accounts";
import { areMandatoryRequiredDocsComplete } from "components/Company/Documents/financialDocumentHelpers";
import { BookMeetingTabParam } from "helpers/bookMeetingConstants";
import { buildBookMeetingUrl } from "helpers/bookMeetingHelpers";
import {
  getOnboardingStepFlags,
  isClientUsersReadyForSubmit,
  isCompanyProfileComplete,
  isDocumentsMandatoryComplete,
  isOnboardingReadyToSubmit,
} from "helpers/onboardingSubmitHelpers";
import { useGetBankAccountsQuery } from "api/api-banks";
import { useGetAccountContactsQuery } from "api/api-users-v2";
import { BUYER, CL_APPROVED, COMPANIES, SELLER } from "codes";
import SkeletonLoad from "components/Common/SkeletonLoad";
import history from "helpers/history";
import useClientUsersDocumentsComplete from "hooks/useClientUsersDocumentsComplete";
import useIsMobile from "hooks/useIsMobile";
import { useState } from "react";
import { toast } from "react-toastify";
import { useAppSelector } from "redux/hooks";

import type { Account } from "types";

const ToDoListing = ({ company }: { company: Account }) => {
  const [open, setOpen] = useState(false);
  const profile = useAppSelector((state) => state.appState.profile);
  const normalizedProfile = String(profile ?? "").toLowerCase();

  const handleClick = () => {
    setOpen(!open);
  };
  const { data: users_v2, isFetching: isFetchingUsers } =
    useGetAccountContactsQuery(company?.id, {
      skip: !Boolean(company?.id) || !Boolean(company),
    });
  const {
    hasClientContacts,
    isClientUsersDocumentsComplete,
    isCheckingClientUserDocuments,
  } = useClientUsersDocumentsComplete(company?.id, users_v2);

  const { data: banks, isFetching: isFetchingBanks } = useGetBankAccountsQuery(
    company?.id,
    {
      refetchOnFocus: true,
      skip: !Boolean(company?.id) || normalizedProfile === BUYER.toLowerCase(),
    },
  );

  const { data: onboardingStatus, isFetching: isFetchingOnboardingStatus } =
    useGetAccountOnboardingStatusQuery(company?.id, {
      skip: !Boolean(company?.id),
    });

  const { data: pendingDocs } = useGetDocumentsQuery(
    { account_id: company?.id },
    { skip: !Boolean(company?.id) },
  );

  const {
    isDocumentsStepComplete,
    isDetailsStepComplete,
    isUsersStepComplete,
    isBankDetailsStepComplete,
  } = getOnboardingStepFlags(onboardingStatus);
  const areRequiredDocumentsUploaded = areMandatoryRequiredDocsComplete(
    pendingDocs?.data,
    {
      account_product: company?.account_product,
      companyType: normalizedProfile,
    },
  );
  const documentsMandatoryComplete = isDocumentsMandatoryComplete({
    isDocumentsStepComplete,
    pendingDocsComplete: pendingDocs?.complete,
    areRequiredDocumentsUploaded,
  });
  const isDocumentsReviewed =
    company?.document_reviewed_operation === true &&
    company?.document_reviewed_risk === true;

  // Match Company profile > Documents tab: green / pending-review counts as done here
  const documentsCheckPassed =
    documentsMandatoryComplete || isDocumentsReviewed;
  const isUsersReadyForSubmit = isClientUsersReadyForSubmit({
    isUsersStepComplete,
    hasClientContacts,
    isClientUsersDocumentsComplete,
    isCheckingClientUserDocuments,
  });
  const isCompanyReadyToSubmit = isOnboardingReadyToSubmit({
    isProfileComplete: isCompanyProfileComplete({
      isDetailsStepComplete,
      isUsersStepComplete: isUsersReadyForSubmit,
      isBankDetailsStepComplete,
      normalizedProfile,
    }),
    documentsMandatoryComplete,
    isDocumentsReviewed,
  });

  const documentsTodoPrimary = isDocumentsReviewed
    ? "Company documents completed!"
    : documentsMandatoryComplete
      ? "Documents uploaded successfully!"
      : "Please upload the KYC documents";

  // Same 3-state icons as Company profile > Documents tab
  const documentsStatusIcon = isDocumentsReviewed ? (
    <CheckCircleIcon color="success" />
  ) : documentsMandatoryComplete ? (
    <WatchLaterIcon color="action" fontSize="small" />
  ) : (
    <ErrorIcon color="error" />
  );

  const [submitAccount, { isLoading: isLoadingSubmission }] =
    useSubmitAccountMutation();

  const isMobile = useIsMobile();

  const isCompanyAgreed =
    company?.agree_to_terms && company?.agree_to_privacy_policy;

  // Book Meeting logic
  const isCLApproved = company?.credit_limit?.status === CL_APPROVED;
  const callStatus = company?.call_status;

  // Show the booking CTA until CL is approved.
  const shouldShowBookMeeting = !isCLApproved;

  // Enable button based on call_status while CL is still pending.
  const canBookMeeting =
    !isCLApproved && (callStatus === false || callStatus === null);

  const handleBookMeeting = () => {
    window.open(
      buildBookMeetingUrl({
        tab: BookMeetingTabParam.RISK_TEAM,
        accountId: company?.id,
      }),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleSubmission = () => {
    if (isCompanyAgreed) {
      submitAccount({
        accountId: company?.id,
        data: { is_submitted: true },
      })
        .unwrap()
        .catch((error) => {
          const errorFile = error?.message;
          toast(`Something went wrong : ${errorFile}`, {
            type: "error",
          });
        });
    } else {
      history.push(`/submission`, { entity: company, isPartner: false });
    }
  };

  const SubmitButton = () => (
    <LoadingButton
      variant="contained"
      fullWidth
      color="success"
      onClick={handleSubmission}
      disabled={!isCompanyReadyToSubmit}
      loading={isLoadingSubmission}
      data-testid={`submit-mycompany-${company?.name}`}
    >
      SUBMIT
    </LoadingButton>
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <ListItemButton
          onClick={handleClick}
          data-testid={`listitem-mycompany-${company?.name}`}
          sx={{
            pl: 1.5,
            border: "1px solid rgb(224, 224, 224)",
            borderRadius: "10px",
            mb: "0em",
            bgcolor: "rgba(244, 244, 244, 0.7)",
          }}
        >
          <Grid
            container
            spacing={1}
            alignItems="start"
            justifyContent="space-between"
          >
            <Grid item xs={12} lg={shouldShowBookMeeting ? 5 : 8}>
              <Grid
                container
                spacing={1}
                alignItems="end"
                justifyContent="start"
              >
                {!company && <SkeletonLoad bars={2} />}
                {company && (
                  <>
                    <Grid item>
                      <IconButton
                        sx={{
                          borderRadius: "50%",
                          background: "#bcbcbc",
                          boxShadow:
                            "4px 4px 30px 0px #bdbdbd, -15px -17px 30px 0px #ffffff",
                        }}
                      >
                        {open ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <ListItemIcon sx={{ minWidth: 10 }}>
                        {isCompanyReadyToSubmit ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </ListItemIcon>
                    </Grid>
                    <Grid item>
                      <ListItemText
                        primary={`${company?.name}`}
                        primaryTypographyProps={{ fontWeight: "800" }}
                      />
                    </Grid>
                  </>
                )}

                {company && company?.is_submitted && (
                  <Grid item>
                    <Chip
                      label="Submitted"
                      data-testid={`submitted-mycompany-${company?.name}`}
                      color="success"
                      variant="outlined"
                      sx={{
                        fontSize: "0.875em",
                        height: "2.5em",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={shouldShowBookMeeting ? 4 : 6} lg={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => history.push(COMPANIES)}
                data-testid={`view-mycompany-${company?.name}`}
              >
                View
              </Button>
            </Grid>
            {!company?.is_submitted && (
              <Grid item xs={shouldShowBookMeeting ? 4 : 6} lg={2}>
                <SubmitButton />
              </Grid>
            )}
            {shouldShowBookMeeting && canBookMeeting && (
              <Grid item xs={4} lg={3}>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  startIcon={<CalendarMonth />}
                  onClick={handleBookMeeting}
                  data-testid={`book-meeting-${company?.name}`}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Book Meeting
                </Button>
              </Grid>
            )}
            {shouldShowBookMeeting &&
              !canBookMeeting &&
              callStatus === true && (
                <Grid item xs={4} lg={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    color="inherit"
                    startIcon={<CalendarMonth />}
                    disabled
                    data-testid={`meeting-done-${company?.name}`}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Meeting Done
                  </Button>
                </Grid>
              )}
          </Grid>
        </ListItemButton>
      </Grid>
      <Grid item xs={12}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div">
            <ListItemButton
              sx={{
                pl: isMobile ? 1 : 3,
                pr: isMobile ? 1 : 2,
                border: "1px solid rgb(224, 224, 224)",
                borderRadius: "10px",
                mb: "0.5em",
              }}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} lg={10}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={2}>
                      <ListItemIcon>
                        {onboardingStatus?.details?.status ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </ListItemIcon>
                    </Grid>
                    <Grid item xs={10} lg={10}>
                      <ListItemText
                        primary={
                          onboardingStatus?.details?.status
                            ? "Company details completed!"
                            : "Please complete company details."
                        }
                        secondary={onboardingStatus?.details?.info}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {!onboardingStatus?.details?.status && (
                  <Grid item xs={12} lg={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      data-testid={`details-mycompany-${company?.name}`}
                      onClick={() =>
                        history.push(COMPANIES, {
                          tab: 0,
                        })
                      }
                    >
                      ADD
                    </Button>
                  </Grid>
                )}
              </Grid>
            </ListItemButton>
            <ListItemButton
              sx={{
                pl: isMobile ? 1 : 3,
                pr: isMobile ? 1 : 2,
                border: "1px solid rgb(224, 224, 224)",
                borderRadius: "10px",

                mb: "0.5em",
              }}
            >
              {isFetchingOnboardingStatus && <SkeletonLoad bars={1} />}
              {!isFetchingOnboardingStatus && (
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} lg={10}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={2}>
                        <ListItemIcon>{documentsStatusIcon}</ListItemIcon>
                      </Grid>
                      <Grid item>
                        <ListItemText
                          primary={documentsTodoPrimary}
                          secondary={
                            documentsCheckPassed
                              ? !isDocumentsReviewed &&
                                documentsMandatoryComplete
                                ? "Pending for document review by incomlend internal team"
                                : ""
                              : onboardingStatus?.documents?.info
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {!documentsCheckPassed && (
                    <Grid item xs={12} lg={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        data-testid={`documents-mycompany-${company?.name}`}
                        onClick={() =>
                          history.push(COMPANIES, {
                            tab: 1,
                          })
                        }
                      >
                        UPLOAD
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
            </ListItemButton>

            <ListItemButton
              sx={{
                pl: isMobile ? 1 : 3,
                pr: isMobile ? 1 : 2,
                border: "1px solid rgb(224, 224, 224)",
                borderRadius: "10px",

                mb: "0.5em",
              }}
            >
              {isFetchingUsers && <SkeletonLoad bars={1} />}
              {!isFetchingUsers && (
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} lg={10}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={2}>
                        <ListItemIcon>
                          {isUsersReadyForSubmit ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <ErrorIcon color="error" />
                          )}
                        </ListItemIcon>
                      </Grid>
                      <Grid item>
                        <ListItemText
                          primary={
                            !Boolean(users_v2?.length)
                              ? "Please register a user."
                              : isUsersReadyForSubmit
                                ? "Primary Contact uploaded successfully!"
                                : isCheckingClientUserDocuments
                                  ? "Checking contact documents..."
                                  : hasClientContacts &&
                                      !isClientUsersDocumentsComplete
                                    ? "Please upload required contact documents (ID/Passport or Letter of Authorization)."
                                    : "Please complete contact details."
                          }
                          secondary={onboardingStatus?.users?.info}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {Boolean(users_v2?.length) && !isUsersReadyForSubmit && (
                    <Grid item xs={12} lg={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => history.push(`/users`)}
                        data-testid={`users-mycompany-${company?.name}`}
                      >
                        COMPLETE
                      </Button>
                    </Grid>
                  )}
                  {!Boolean(users_v2?.length) && (
                    <Grid item xs={12} lg={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => history.push(`/users`)}
                        data-testid={`users-mycompany-${company?.name}`}
                      >
                        REGISTER
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
            </ListItemButton>
            {normalizedProfile === SELLER && (
              <ListItemButton
                sx={{
                  pl: isMobile ? 1 : 3,
                  pr: isMobile ? 1 : 2,
                  border: "1px solid rgb(224, 224, 224)",
                  borderRadius: "10px",

                  mb: "0.5em",
                }}
              >
                {isFetchingBanks && <SkeletonLoad bars={1} />}
                {!isFetchingBanks && (
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} lg={10}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={2}>
                          <ListItemIcon>
                            {onboardingStatus?.bank_details?.status ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <ErrorIcon color="error" />
                            )}
                          </ListItemIcon>
                        </Grid>
                        <Grid item>
                          <ListItemText
                            primary={
                              Boolean(banks?.length)
                                ? onboardingStatus?.bank_details?.status
                                  ? "Bank account registered!"
                                  : "Please enter Bank Account details."
                                : "Please register a bank account."
                            }
                            secondary={onboardingStatus?.bank_details?.info}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    {Boolean(banks?.length) &&
                      !onboardingStatus?.bank_details?.status && (
                        <Grid item xs={12} lg={2}>
                          <Button
                            variant="contained"
                            fullWidth
                            data-testid={`banks-mycompany-complete-${company?.name}`}
                            onClick={() =>
                              history.push(COMPANIES, {
                                tab: 2,
                              })
                            }
                          >
                            COMPLETE
                          </Button>
                        </Grid>
                      )}
                    {!Boolean(banks?.length) && (
                      <Grid item xs={12} lg={2}>
                        <Button
                          variant="contained"
                          fullWidth
                          data-testid={`banks-mycompany-${company?.name}`}
                          onClick={() =>
                            history.push(COMPANIES, {
                              tab: 2,
                            })
                          }
                        >
                          REGISTER
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                )}
              </ListItemButton>
            )}
          </List>
        </Collapse>
      </Grid>
    </Grid>
  );
};
export default ToDoListing;
